import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { ReactComponent as DeleteIcon } from "../../../../assects/svg/client_details/delete.svg";
import CustomDropdown, {
  DropdownOption,
} from "../../../Ui_Components/CustomDropdown";
import { Button } from "../../../Ui_Components/Button";
import useCustomScrollbar from "../../../styles/useCustomScrollbar";
import {
  calculateCPFRatio,
  formatCPFRatio,
} from "../../../Plans/Meal_Creation/CPFCalculator";
import { searchAllIngredientsAdmin } from "../../../../services/ingredients";
import { BASE_URL } from "../../../api/url";

// Per-100g nutrition baseline the row uses to scale as the user tweaks
// quantity / unit. Populated from the ingredient search row on select.
// `protein` is AAFAdj_protein (A.pro); `grossProtein` is gross_protein —
// the backend tracks them separately and both are displayed in the totals
// strips.
export interface IngredientNutrition {
  cal: number;
  protein: number;
  grossProtein: number;
  fat: number;
  carb: number;
  netCarb: number;
  gl: number;
}

export interface IngredientMeasurement {
  unit_type: string;
  measure_gram: number;
}

interface CalculatorIngredientRow {
  id: string;
  name: string;
  quantity: string;
  unit: string;
  imageUrl: string | null;
  oil: string;
  cal: string;
  gl: string;
  netCarb: string;
  carb: string;
  protein: string;
  // Row cells only display A.protein; gross protein isn't in the table but
  // is tracked here so the totals strip can sum it separately across rows.
  grossProtein: string;
  fat: string;
  cpf: string;
  // Selected ingredient's identity + scaling data, populated once the user
  // picks a row from the autocomplete. The nutrition cells above are
  // derived from these + quantity + unit each time either changes.
  foodId?: number;
  baseQuantity?: number;
  measurements?: IngredientMeasurement[];
  nutritionPer100g?: IngredientNutrition;
}

const createId = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const createEmptyRow = (): CalculatorIngredientRow => ({
  id: createId(),
  name: "",
  quantity: "",
  unit: "",
  imageUrl: null,
  oil: "",
  cal: "",
  gl: "",
  netCarb: "",
  carb: "",
  protein: "",
  grossProtein: "",
  fat: "",
  cpf: "",
});

const numeric = (v: string) => v.replace(/[^0-9.]/g, "");

// Row nutrition cells arrive as strings ("00", "12.5", "") — coerce to a
// finite number so sums and the CPF math don't have to guard everywhere.
export const toNumber = (v: string): number => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

// Convenience for the totals strip: caps precision at 2dp and strips
// trailing zeros so "12" stays "12g", "12.5" stays "12.5g", and "12.567"
// rounds to "12.57g". "0g" for the empty/starting state so the display
// doesn't show "NaNg" or padded zeros before the user enters anything.
const formatGrams = (n: number): string => {
  if (!Number.isFinite(n) || n === 0) return "0g";
  return `${Number(n.toFixed(2))}g`;
};

// Same formatter without the "g" suffix — GL is a unitless index, not a
// gram amount, so it renders as a bare number ("12.5" / "0").
const formatNumber = (n: number): string => {
  if (!Number.isFinite(n) || n === 0) return "0";
  return String(Number(n.toFixed(2)));
};

// Ingredient pictures come back as either "/media/..." or an absolute URL;
// prefix BASE_URL so relative paths resolve against the API host.
const resolvePictureUrl = (picture: unknown): string | null => {
  if (!picture || typeof picture !== "string") return null;
  if (/^https?:\/\//i.test(picture)) return picture;
  return `${BASE_URL}${picture.startsWith("/") ? "" : "/"}${picture}`;
};

// Cap the row-level nutrition cells at 2dp and strip trailing zeros so
// whole numbers render bare ("12"), half-values keep one decimal
// ("12.5"), and long floats round in ("12.567" → "12.57"). Zero is
// returned as empty so the row's placeholder ("00") can show through.
export const formatCell = (n: number): string => {
  if (!Number.isFinite(n) || n === 0) return "";
  return String(Number(n.toFixed(2)));
};

/* Scale the ingredient's per-base-quantity nutrition down to a specific
   quantity + unit. The base quantity is what the backend recorded the
   nutrition against (typically 100g), and each measurement carries a
   `measure_gram` factor that converts one unit of that measurement into
   grams. Multiplier = (quantity * measure_gram) / base_quantity.
   Exposed as a plain function so both the calculator's row and the
   Recipe & ingredients table (with its own row shape) can call it. */
export const computeRowNutrition = (params: {
  quantity: string;
  unit: string;
  measurements?: IngredientMeasurement[];
  baseQuantity?: number;
  nutritionPer100g?: IngredientNutrition;
}): IngredientNutrition => {
  const zero: IngredientNutrition = {
    cal: 0,
    protein: 0,
    grossProtein: 0,
    fat: 0,
    carb: 0,
    netCarb: 0,
    gl: 0,
  };
  const per = params.nutritionPer100g;
  if (!per) return zero;
  const quantity = toNumber(params.quantity);
  if (quantity === 0) return zero;
  const base =
    params.baseQuantity && params.baseQuantity > 0 ? params.baseQuantity : 100;
  const measurement = params.measurements?.find(
    (m) => m.unit_type === params.unit,
  );
  const gramsPerUnit = measurement?.measure_gram ?? 0;
  if (!gramsPerUnit) return zero;
  const multiplier = (quantity * gramsPerUnit) / base;
  return {
    cal: per.cal * multiplier,
    protein: per.protein * multiplier,
    grossProtein: per.grossProtein * multiplier,
    fat: per.fat * multiplier,
    carb: per.carb * multiplier,
    netCarb: per.netCarb * multiplier,
    gl: per.gl * multiplier,
  };
};

const isRowEmpty = (row: CalculatorIngredientRow) =>
  !row.name && !row.quantity && !row.unit;

// Two grid templates: identical columns (Name, Units, Quantity, Cal, GL,
// Net carb, Carb, A.Protein, Fat, C:P:F); entry adds a trailing delete
// slot. Units is the widest editable slot so long measurement labels
// ("Piece (medium) - 125gm") render on a single line without wrapping;
// Quantity stays at 0.9fr so its input placeholder renders in full.
const ENTRY_GRID =
  "grid-cols-[2.4fr_2.0fr_0.9fr_0.5fr_0.5fr_0.8fr_0.5fr_0.7fr_0.5fr_1.1fr_0.4fr]";
const REVIEW_GRID =
  "grid-cols-[2.4fr_2.0fr_0.9fr_0.5fr_0.5fr_0.8fr_0.5fr_0.7fr_0.5fr_1.1fr]";

// Search result — the admin ingredient endpoint already carries everything
// the calculator needs: nutrition baseline, base_quantity, and the row's
// full measurement list. Populating all of it here means selection can go
// straight from search hit → fully-usable row without a follow-up details
// fetch.
export interface IngredientSuggestion {
  foodId: number;
  name: string;
  imageUrl: string | null;
  baseQuantity: number;
  defaultUnit: string;
  measurements: IngredientMeasurement[];
  nutritionPer100g: IngredientNutrition;
}

export const mapSuggestion = (raw: any): IngredientSuggestion => {
  const measurementsRaw: any[] = Array.isArray(raw?.measurements)
    ? raw.measurements
    : [];
  const measurements: IngredientMeasurement[] = measurementsRaw
    .map((m) => ({
      unit_type: String(m?.unit_type ?? m?.measure_unit_type ?? ""),
      measure_gram: toNumber(String(m?.measure_gram ?? "")),
    }))
    .filter((m) => m.unit_type);
  return {
    foodId: Number(raw?.food_id),
    name: String(raw?.item_name ?? ""),
    imageUrl: resolvePictureUrl(raw?.picture),
    baseQuantity: toNumber(String(raw?.base_quantity ?? "")) || 100,
    defaultUnit: String(raw?.units ?? "").trim(),
    measurements,
    nutritionPer100g: {
      cal: toNumber(String(raw?.cals ?? "")),
      protein: toNumber(String(raw?.AAFAdj_protein ?? "")),
      grossProtein: toNumber(String(raw?.gross_protein ?? "")),
      fat: toNumber(String(raw?.total_fat ?? "")),
      carb: toNumber(String(raw?.carbs ?? "")),
      netCarb: toNumber(String(raw?.net_carbs ?? "")),
      gl: toNumber(String(raw?.GL ?? "")),
    },
  };
};

export interface IngredientAutocompleteProps {
  value: string;
  onSelect: (suggestion: IngredientSuggestion) => void;
  onClear: () => void;
  placeholder?: string;
}

/* Debounced ingredient search backed by GET_ALL_INGREDIENTS_ADMIN. The
   dropdown renders through a portal so overflow-hidden ancestors (the
   modal's scroll container, table cells) don't clip it. Selection carries
   nutrition baseline + measurements + base_quantity, so callers get a
   fully-usable ingredient row without any follow-up fetch. */
export const IngredientAutocomplete: React.FC<IngredientAutocompleteProps> = ({
  value,
  onSelect,
  onClear,
  placeholder = "Search ingredient",
}) => {
  const [input, setInput] = useState(value);
  const [results, setResults] = useState<IngredientSuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pos, setPos] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    setInput(value);
  }, [value]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    }
  }, [isOpen, results.length]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (abortRef.current) abortRef.current.abort();
    },
    [],
  );

  const runSearch = (query: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();
    const trimmed = query.trim();
    if (!trimmed) {
      setResults([]);
      setIsOpen(false);
      setLoading(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      try {
        const api = await searchAllIngredientsAdmin(trimmed, 20);
        if (ctrl.signal.aborted) return;
        // Response shape varies (top-level array vs { data / food_data /
        // ingredients / results: [...] }) — try each in turn so this doesn't
        // silently break if the backend key changes.
        const rows: any[] = Array.isArray(api)
          ? api
          : Array.isArray(api?.data)
            ? api.data
            : Array.isArray(api?.food_data)
              ? api.food_data
              : Array.isArray(api?.ingredients)
                ? api.ingredients
                : Array.isArray(api?.results)
                  ? api.results
                  : [];
        const mapped = rows.map(mapSuggestion).filter((s) => s.foodId);
        setResults(mapped);
        setIsOpen(mapped.length > 0);
      } catch (err) {
        if ((err as any)?.name === "AbortError") return;
        setResults([]);
        setIsOpen(false);
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, 300);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setInput(v);
    runSearch(v);
    if (!v) onClear();
  };

  const handleBlur = () => {
    // Delay close so mousedown on a suggestion still registers.
    setTimeout(() => setIsOpen(false), 150);
  };

  return (
    <div className="relative w-full">
      <input
        ref={inputRef}
        type="text"
        value={input}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={loading ? "Searching…" : placeholder}
        autoComplete="off"
        className="w-full px-2 py-2 border border-[#E5E5EA] rounded-md text-sm outline-none focus:border-[#0DB5B5] bg-white"
      />
      {isOpen &&
        results.length > 0 &&
        pos &&
        ReactDOM.createPortal(
          <div
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: pos.width,
              zIndex: 9999,
            }}
            className="bg-white border border-[#E5E5EA] rounded-md shadow-lg max-h-[220px] overflow-y-auto"
          >
            {results.map((r) => (
              <div
                key={r.foodId}
                onMouseDown={() => {
                  setInput(r.name);
                  setIsOpen(false);
                  setResults([]);
                  onSelect(r);
                }}
                className="px-3 py-2 cursor-pointer text-sm text-[#1F1F1F] hover:bg-[#F5F5F5]"
              >
                <span className="truncate block">{r.name}</span>
              </div>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
};

// Everything the parent form needs to hydrate its macro fields + Recipe &
// ingredients table when the user clicks "Apply to dish". Macros are the
// per-100g cooked values (raw numbers so the parent can round/format as
// its own inputs need); `rowCpf` is the per-row ratio map so the parent
// doesn't have to recompute it.
export interface DishCalculatorApplyPayload {
  rows: CalculatorIngredientRow[];
  cookedTotals: {
    cal: number;
    protein: number;
    grossProtein: number;
    fat: number;
    carb: number;
    netCarb: number;
    gl: number;
  };
  rowCpf: Record<string, string>;
}

interface DishCalculatorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply?: (payload: DishCalculatorApplyPayload) => void;
  // Rows to pre-populate the calculator with when it opens. Backs the Edit
  // dish flow — the recipe already has ingredients + quantities, so the
  // calculator should reflect them (with computed nutrition) rather than
  // starting empty. Undefined / empty falls back to a single empty row.
  initialRows?: CalculatorIngredientRow[];
  // Pre-populated cooked quantity for the Edit flow. Optional — falls back
  // to blank so the review gate still requires the user to confirm it.
  initialCookedQuantity?: string;
}

export type { CalculatorIngredientRow };

const DishCalculatorModal: React.FC<DishCalculatorModalProps> = ({
  isOpen,
  onClose,
  onApply,
  initialRows,
  initialCookedQuantity,
}) => {
  useCustomScrollbar();
  const [step, setStep] = useState<"entry" | "review">("entry");
  const [rows, setRows] = useState<CalculatorIngredientRow[]>([createEmptyRow()]);
  const [cookedQuantity, setCookedQuantity] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Reset internal state whenever the modal opens fresh. Edit flows pass
  // `initialRows` so the calculator opens pre-populated with the dish's
  // current recipe; add flows leave it undefined and get a single empty
  // starter row.
  useEffect(() => {
    if (isOpen) {
      setStep("entry");
      setRows(
        initialRows && initialRows.length > 0
          ? initialRows.map((r) => ({ ...r }))
          : [createEmptyRow()],
      );
      setCookedQuantity(initialCookedQuantity ?? "");
      setConfirmDeleteId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Per-row C:P:F strings, keyed by row id, computed from the row's own
  // carb/protein/fat/cal values via the shared CPFCalculator (same formula
  // CreateDishModal uses). Empty rows collapse to "0:0:0". Kept above the
  // `!isOpen` early return so hook order stays stable across renders.
  const rowCpf = useMemo(() => {
    const map: Record<string, string> = {};
    rows.forEach((row) => {
      const result = calculateCPFRatio(
        toNumber(row.carb),
        toNumber(row.protein),
        toNumber(row.fat),
        toNumber(row.cal),
      );
      map[row.id] = formatCPFRatio(result.ratio);
    });
    return map;
  }, [rows]);

  // Totals sum across every visible row. Which set of rows counts depends on
  // step: the entry view shows a live-summing preview across all rows the
  // user is editing; the review view narrows to rows that have name +
  // quantity + unit filled in.
  const totalsRaw = useMemo(() => {
    const source =
      step === "review"
        ? rows.filter((r) => r.name && r.quantity && r.unit)
        : rows;
    return source.reduce(
      (acc, r) => ({
        cal: acc.cal + toNumber(r.cal),
        carb: acc.carb + toNumber(r.carb),
        netCarb: acc.netCarb + toNumber(r.netCarb),
        fat: acc.fat + toNumber(r.fat),
        protein: acc.protein + toNumber(r.protein),
        grossProtein: acc.grossProtein + toNumber(r.grossProtein),
        gl: acc.gl + toNumber(r.gl),
      }),
      {
        cal: 0,
        carb: 0,
        netCarb: 0,
        fat: 0,
        protein: 0,
        grossProtein: 0,
        gl: 0,
      },
    );
  }, [rows, step]);

  if (!isOpen) return null;

  // Re-derive the row's nutrition cells from its ingredient baseline +
  // quantity + unit. Called whenever any of those change so the totals
  // strip and CPF stay in sync.
  const patchRowWithNutrition = (
    id: string,
    patch: Partial<CalculatorIngredientRow>,
  ) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const merged = { ...r, ...patch };
        const n = computeRowNutrition(merged);
        return {
          ...merged,
          cal: formatCell(n.cal),
          protein: formatCell(n.protein),
          grossProtein: formatCell(n.grossProtein),
          fat: formatCell(n.fat),
          carb: formatCell(n.carb),
          netCarb: formatCell(n.netCarb),
          gl: formatCell(n.gl),
        };
      }),
    );
  };

  const clearRow = (id: string) => {
    setRows((prev) =>
      prev.map((r) =>
        r.id === id
          ? {
              ...createEmptyRow(),
              id: r.id,
            }
          : r,
      ),
    );
  };

  // The admin search response already carries base_quantity + measurements +
  // full nutrition, so selection can wire the row up in one pass — no
  // follow-up details fetch needed. Pick an initial unit that maps to a
  // real measurement so the nutrition compute doesn't sit at zero.
  const handleIngredientSelect = (
    rowId: string,
    suggestion: IngredientSuggestion,
  ) => {
    const { measurements, defaultUnit } = suggestion;
    const hasDefault = measurements.some((m) => m.unit_type === defaultUnit);
    const initialUnit = hasDefault
      ? defaultUnit
      : measurements.find(
          (m) => m.unit_type === "gm" || m.unit_type === "ml",
        )?.unit_type ??
        measurements[0]?.unit_type ??
        "";
    patchRowWithNutrition(rowId, {
      name: suggestion.name,
      imageUrl: suggestion.imageUrl,
      foodId: suggestion.foodId,
      baseQuantity: suggestion.baseQuantity,
      measurements,
      nutritionPer100g: suggestion.nutritionPer100g,
      unit: initialUnit,
    });
  };

  const requestDelete = (row: CalculatorIngredientRow) => {
    // Preserve the "at least one row" invariant: no confirm prompt when the
    // sole row has nothing in it — deleting it would just re-create it.
    if (rows.length === 1 && isRowEmpty(row)) return;
    setConfirmDeleteId(row.id);
  };

  const confirmDelete = (id: string) => {
    const remaining = rows.filter((r) => r.id !== id);
    setRows(remaining.length === 0 ? [createEmptyRow()] : remaining);
    setConfirmDeleteId(null);
  };

  const addRow = () => setRows((prev) => [...prev, createEmptyRow()]);

  const totalRawQuantity = rows.reduce((sum, r) => {
    const n = Number(r.quantity);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);

  const filledRows = rows.filter((r) => r.name && r.quantity && r.unit);
  // Review is gated on both at least one usable ingredient row AND a
  // cooked quantity — the review strip is meaningless without either.
  const canReview =
    filledRows.length > 0 && toNumber(cookedQuantity) > 0;

  const handleReview = () => {
    if (!canReview) return;
    setStep("review");
  };

  const handleBack = () => setStep("entry");

  const handleApply = () => {
    onApply?.({
      rows: filledRows,
      cookedTotals: {
        cal: cookedRaw.cal,
        protein: cookedRaw.protein,
        grossProtein: cookedRaw.grossProtein,
        fat: cookedRaw.fat,
        carb: cookedRaw.carb,
        netCarb: cookedRaw.netCarb,
        gl: cookedRaw.gl,
      },
      rowCpf,
    });
    onClose();
  };

  const totalsCpf = formatCPFRatio(
    calculateCPFRatio(
      totalsRaw.carb,
      totalsRaw.protein,
      totalsRaw.fat,
      totalsRaw.cal,
    ).ratio,
  );
  // A.pro sums the adjusted protein (AAFAdj_protein) and Gross pro sums the
  // gross_protein field — both come off each ingredient's per-100g baseline
  // so they scale independently as quantity + unit change.
  const totals = {
    cal: totalsRaw.cal === 0 ? "0" : String(Number(totalsRaw.cal.toFixed(2))),
    carb: formatGrams(totalsRaw.carb),
    netCarb: formatGrams(totalsRaw.netCarb),
    fats: formatGrams(totalsRaw.fat),
    aPro: formatGrams(totalsRaw.protein),
    grossPro: formatGrams(totalsRaw.grossProtein),
    gl: formatNumber(totalsRaw.gl),
    cpf: totalsCpf,
  };

  /* "Total cooked quantity ≈ per 100 g" strip. The top strip already sums
     the raw ingredient nutrition — after cooking, that same amount of
     nutrition is now packed into whatever cooked weight the user reports.
     Scale it down to per-100g by (100 / cookedQuantity) so this strip
     reads like a nutrition label. Kept blank (zeros) until the user
     enters a cooked quantity so the display doesn't lie by echoing the
     top strip. */
  const cookedQty = toNumber(cookedQuantity);
  const hasCookedQty = cookedQty > 0;
  const cookedScale = hasCookedQty ? 100 / cookedQty : 0;
  const cookedRaw = {
    cal: totalsRaw.cal * cookedScale,
    carb: totalsRaw.carb * cookedScale,
    netCarb: totalsRaw.netCarb * cookedScale,
    fat: totalsRaw.fat * cookedScale,
    protein: totalsRaw.protein * cookedScale,
    grossProtein: totalsRaw.grossProtein * cookedScale,
    gl: totalsRaw.gl * cookedScale,
  };
  const cookedCpf = hasCookedQty
    ? formatCPFRatio(
        calculateCPFRatio(
          cookedRaw.carb,
          cookedRaw.protein,
          cookedRaw.fat,
          cookedRaw.cal,
        ).ratio,
      )
    : "0:0:0";
  const cookedTotals = {
    cal: hasCookedQty && cookedRaw.cal > 0
      ? String(Number(cookedRaw.cal.toFixed(2)))
      : "0",
    carb: formatGrams(cookedRaw.carb),
    netCarb: formatGrams(cookedRaw.netCarb),
    fats: formatGrams(cookedRaw.fat),
    aPro: formatGrams(cookedRaw.protein),
    grossPro: formatGrams(cookedRaw.grossProtein),
    gl: formatNumber(cookedRaw.gl),
    cpf: cookedCpf,
  };

  const gridCols = step === "entry" ? ENTRY_GRID : REVIEW_GRID;
  const visibleRows = step === "review" ? filledRows : rows;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-md w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex-1 overflow-y-auto p-6">
          {/* Header */}
          <div className="mb-4 border-b-[0.5px] border-[#E5E5EA] pb-3">
            <h2 className="text-[18px] font-medium text-[#1F1F1F]">
              Dish calculator
            </h2>
          </div>

          {/* Totals strip */}
          <div className="flex items-center justify-between gap-8 mb-4 border-[0.5px] border-[#E5E5EA] rounded-md px-4 py-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#1F1F1F]">Cal -</span>
              <span className="text-sm font-medium text-[#B44AE6]">
                {totals.cal}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#1F1F1F]">Carb -</span>
              <span className="text-sm font-medium text-[#FFB532]">
                {totals.carb}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#1F1F1F]">Net Carb -</span>
              <span className="text-sm font-medium text-[#FF7A5C]">
                {totals.netCarb}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#1F1F1F]">Fats -</span>
              <span className="text-sm font-medium text-[#F25BEC]">
                {totals.fats}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#1F1F1F]">A.pro -</span>
              <span className="text-sm font-medium text-[#6BBDFA]">
                {totals.aPro}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#1F1F1F]">Gross pro -</span>
              <span className="text-sm font-medium text-[#6BBDFA]">
                {totals.grossPro}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#1F1F1F]">GL -</span>
              <span className="text-sm font-medium text-[#A0CA07]">
                {totals.gl}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-[#1F1F1F]">C:P:F</span>
              <span className="text-sm font-medium text-[#007AFF]">
                {totals.cpf}
              </span>
            </div>
          </div>

          {/* Ingredient list */}
          <div className="mb-4 h-[280px] bg-[#FAFAFA] p-4 rounded-md overflow-y-auto custom-scrollbar-overlay pr-2">
            {/* Column headers */}
            <div
              className={`grid ${gridCols} gap-3 mb-3 text-[12px] 3xl:text-[14px] text-[#646464] font-inter font-medium border-b-[0.5px] border-[#E5E5EA] pb-3`}
            >
              <div>Name</div>
              <div>Units</div>
              <div>Quantity</div>
              <div className="text-center">Cal</div>
              <div className="text-center">GL</div>
              <div className="text-center">Net carb</div>
              <div className="text-center">Carb</div>
              <div className="text-center">A.Protein</div>
              <div className="text-center">Fat</div>
              <div className="text-center">C:P:F</div>
              {step === "entry" && <div />}
            </div>

            {visibleRows.map((row) => {
              if (step === "entry" && confirmDeleteId === row.id) {
                return (
                  <div
                    key={row.id}
                    className="flex items-center justify-between mb-2 px-3 py-[6px] bg-[#FFF5F5] border border-[#FFCCC9] rounded-md"
                  >
                    <span className="text-sm text-[#1F1F1F] truncate mr-4">
                      Delete{" "}
                      <span className="font-medium">
                        &quot;{row.name || "this ingredient"}&quot;
                      </span>
                      ?
                    </span>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="danger"
                        size="xs"
                        onClick={() => confirmDelete(row.id)}
                        className="whitespace-nowrap"
                      >
                        Confirm delete
                      </Button>
                      <button
                        type="button"
                        onClick={() => setConfirmDeleteId(null)}
                        className="text-[#8E8E93] hover:text-[#1F1F1F] transition-colors p-1 rounded border-[0.5px] border-[#1F1F1F]"
                      >
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                          <path
                            d="M10.5 3.5L3.5 10.5M3.5 3.5L10.5 10.5"
                            stroke="currentColor"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              }

              if (step === "review") {
                // Mirror the entry dropdown label ("ml - 1gm") so the
                // review reads consistently with what the user picked.
                const measurement = row.measurements?.find(
                  (m) => m.unit_type === row.unit,
                );
                const unitLabel = measurement
                  ? `${measurement.unit_type} - ${measurement.measure_gram}gm`
                  : row.unit;
                return (
                  <div
                    key={row.id}
                    className={`grid ${gridCols} gap-3 mb-2 items-center`}
                  >
                    <div className="min-w-0">
                      <span className="text-sm text-[#1F1F1F] truncate">
                        {row.name}
                      </span>
                    </div>
                    <div className="text-sm text-[#1F1F1F]">{unitLabel}</div>
                    <div className="text-sm text-[#1F1F1F]">{row.quantity}</div>
                    <div className="text-sm text-center text-[#1F1F1F]">
                      {row.cal || "00"}
                    </div>
                    <div className="text-sm text-center text-[#1F1F1F]">
                      {row.gl || "00"}
                    </div>
                    <div className="text-sm text-center text-[#1F1F1F]">
                      {row.netCarb || "00"}
                    </div>
                    <div className="text-sm text-center text-[#1F1F1F]">
                      {row.carb || "00"}
                    </div>
                    <div className="text-sm text-center text-[#1F1F1F]">
                      {row.protein || "00"}
                    </div>
                    <div className="text-sm text-center text-[#1F1F1F]">
                      {row.fat || "00"}
                    </div>
                    <div className="text-sm text-center text-[#1F1F1F] whitespace-nowrap">
                      {rowCpf[row.id] ?? "0:0:0"}
                    </div>
                  </div>
                );
              }

              const disabledDelete = rows.length === 1 && isRowEmpty(row);
              // Unit options come from the picked ingredient's measurements
              // (each carries its own measure_gram, driving the conversion).
              // Empty until an ingredient is picked — the dropdown then
              // stays disabled so the user can't select a mismatched unit.
              const rowUnitOptions = (row.measurements ?? []).map((m) => ({
                value: m.unit_type,
                label: `${m.unit_type} - ${m.measure_gram}gm`,
              }));
              return (
                <div
                  key={row.id}
                  className={`grid ${gridCols} gap-3 mb-2 items-center`}
                >
                  <IngredientAutocomplete
                    value={row.name}
                    onSelect={(sug) => handleIngredientSelect(row.id, sug)}
                    onClear={() => clearRow(row.id)}
                  />
                  <CustomDropdown
                    options={rowUnitOptions}
                    value={row.unit}
                    onChange={(v) =>
                      patchRowWithNutrition(row.id, { unit: v })
                    }
                    placeholder="Select unit"
                    disabled={rowUnitOptions.length === 0}
                    searchable={false}
                  />
                  <input
                    type="text"
                    inputMode="decimal"
                    value={row.quantity}
                    onChange={(e) =>
                      patchRowWithNutrition(row.id, {
                        quantity: numeric(e.target.value),
                      })
                    }
                    placeholder="Quantity"
                    className="w-full px-2 py-2 border border-[#E5E5EA] rounded-md text-sm outline-none focus:border-[#0DB5B5] bg-white"
                  />
                  <div className="text-sm text-center text-[#1F1F1F]">
                    {row.cal || "00"}
                  </div>
                  <div className="text-sm text-center text-[#1F1F1F]">
                    {row.gl || "00"}
                  </div>
                  <div className="text-sm text-center text-[#1F1F1F]">
                    {row.netCarb || "00"}
                  </div>
                  <div className="text-sm text-center text-[#1F1F1F]">
                    {row.carb || "00"}
                  </div>
                  <div className="text-sm text-center text-[#1F1F1F]">
                    {row.protein || "00"}
                  </div>
                  <div className="text-sm text-center text-[#1F1F1F]">
                    {row.fat || "00"}
                  </div>
                  <div className="text-sm text-center text-[#1F1F1F] whitespace-nowrap">
                    {rowCpf[row.id] ?? "0:0:0"}
                  </div>
                  <div className="flex justify-center items-center">
                    <button
                      type="button"
                      onClick={() => requestDelete(row)}
                      disabled={disabledDelete}
                      title={
                        disabledDelete ? "Nothing to delete" : "Delete row"
                      }
                      className={`transition-colors ${
                        disabledDelete
                          ? "text-[#D1D1D6] cursor-not-allowed"
                          : "text-[#FF3B30] hover:text-red-700 cursor-pointer"
                      }`}
                    >
                      <DeleteIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                    </button>
                  </div>
                </div>
              );
            })}

            {step === "entry" && (
              <button
                type="button"
                onClick={addRow}
                className="flex items-center gap-1.5 text-[#0DB5B5] text-sm font-medium hover:text-[#0CA0A0] transition-colors mt-3"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path
                    d="M8 3.5V12.5M3.5 8H12.5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
                Add ingredient
              </button>
            )}
          </div>

          {/* Raw + cooked totals row */}
          <div className="grid grid-cols-1 lg:grid-cols-[140px_1fr] gap-3 items-stretch">
            <div
              className="bg-[#F9F9F9] rounded-md px-4 py-3 flex flex-col justify-center"
              style={{ border: "0.5px solid #8D8D8D" }}
            >
              <span className="text-xs text-[#8E8E93]">Total raw quantity</span>
              <span className="mt-1 text-[20px] font-semibold text-[#1F1F1F]">
                {totalRawQuantity || 0}
                <span className="text-sm text-[#8E8E93] font-normal ml-0.5">
                  (g)
                </span>
              </span>
            </div>

            <div
              className="bg-[#F1FBFB] rounded-md px-4 py-3"
              style={{ border: "0.5px solid #0DB5B5" }}
            >
              <div className="flex items-center justify-between gap-4">
                <span className="text-xs text-[#8E8E93]">
                  Total cooked quantity{" "}
                  <span className="text-[#C7C7CC]">≈ per 100 g</span>
                </span>
                {step === "entry" ? (
                  <div className="relative w-[110px]">
                    <input
                      type="text"
                      inputMode="decimal"
                      value={cookedQuantity}
                      onChange={(e) =>
                        setCookedQuantity(numeric(e.target.value))
                      }
                      className="w-full text-right pr-8 pl-2 py-1.5 border border-[#E5E5EA] rounded-md text-sm outline-none focus:border-[#0DB5B5]"
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-sm text-[#8E8E93] pointer-events-none">
                      (g)
                    </span>
                  </div>
                ) : (
                  <span className="text-[20px] font-semibold text-[#1F1F1F]">
                    {cookedQuantity || 200}
                    <span className="text-sm text-[#8E8E93] font-normal ml-0.5">
                      (g)
                    </span>
                  </span>
                )}
              </div>
              <div className="mt-2 flex items-center justify-between gap-8 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#1F1F1F]">Cal -</span>
                  <span className="text-sm font-medium text-[#B44AE6]">
                    {cookedTotals.cal}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#1F1F1F]">Carb -</span>
                  <span className="text-sm font-medium text-[#FFB532]">
                    {cookedTotals.carb}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#1F1F1F]">Net Carb -</span>
                  <span className="text-sm font-medium text-[#FF7A5C]">
                    {cookedTotals.netCarb}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#1F1F1F]">Fats -</span>
                  <span className="text-sm font-medium text-[#F25BEC]">
                    {cookedTotals.fats}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#1F1F1F]">A.pro -</span>
                  <span className="text-sm font-medium text-[#6BBDFA]">
                    {cookedTotals.aPro}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#1F1F1F]">Gross pro -</span>
                  <span className="text-sm font-medium text-[#6BBDFA]">
                    {cookedTotals.grossPro}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#1F1F1F]">GL -</span>
                  <span className="text-sm font-medium text-[#A0CA07]">
                    {cookedTotals.gl}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#1F1F1F]">C:P:F -</span>
                  <span className="text-sm font-medium text-[#007AFF]">
                    {cookedTotals.cpf}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-[#E5E5EA] p-4 flex justify-end gap-3">
          {step === "entry" ? (
            <>
              <Button variant="secondary" size="md" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="md"
                onClick={handleReview}
                disabled={!canReview}
              >
                Review
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" size="md" onClick={handleBack}>
                Back
              </Button>
              <Button variant="primary" size="md" onClick={handleApply}>
                Apply to dish
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DishCalculatorModal;
